import {
  Armchair,
  ArrowDown,
  CalendarDays,
  Camera,
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  ExternalLink,
  Gift as GiftIcon,
  Heart,
  Home,
  Images,
  MapPin,
  Menu,
  MessageCircleHeart,
  PersonStanding,
  Phone,
  ScanLine,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Users,
  Wheat,
  X,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import {
  type CSSProperties,
  type FormEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { api } from "../api";
import { Brand } from "../components/Brand";
import { Countdown } from "../components/Countdown";
import { Dialog } from "../components/Dialog";
import type { Gift, PublicData, SeatingTable } from "../types";
import { isWediaryConfigured } from "../wediary";

type StoryPhotoDefinition = {
  name: string;
  alt: string;
  width: number;
  height: number;
  caption?: string;
  variant?: "family" | "portrait";
};

const storyPhotos: StoryPhotoDefinition[] = [
  {
    name: "story-01",
    alt: "Markéta na koni",
    caption: "Markéta",
    width: 640,
    height: 800,
  },
  {
    name: "story-03",
    alt: "Tomáš",
    caption: "Tomáš",
    width: 640,
    height: 800,
  },
  { name: "story-02", alt: "Chvíle u koní", width: 640, height: 800 },
  {
    name: "story-04",
    alt: "Dětská momentka u poníka",
    width: 640,
    height: 800,
  },
  { name: "story-05", alt: "Květinová momentka", width: 640, height: 800 },
  {
    name: "story-06",
    alt: "Společná chvíle s poníkem",
    width: 640,
    height: 800,
  },
];

const featuredStoryPhotos: StoryPhotoDefinition[] = [
  {
    name: "story-08",
    alt: "Společná rodinná momentka dětí",
    caption: "Naše parta",
    width: 1200,
    height: 800,
    variant: "family",
  },
  {
    name: "story-07",
    alt: "Prázdninová momentka u moře",
    width: 640,
    height: 800,
    variant: "portrait",
  },
  {
    name: "story-09",
    alt: "Markéta a Tomáš",
    caption: "My dva",
    width: 800,
    height: 1000,
    variant: "portrait",
  },
];

function StoryPhotoCard({
  photo,
  featured = false,
}: {
  photo: StoryPhotoDefinition;
  featured?: boolean;
}) {
  const classNames = [
    "story-photo",
    featured ? "story-photo--featured" : "",
    photo.variant ? `story-photo--${photo.variant}` : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <figure className={classNames}>
      <picture>
        <source
          srcSet={`/images/story/${photo.name}.avif`}
          type="image/avif"
        />
        <source
          srcSet={`/images/story/${photo.name}.webp`}
          type="image/webp"
        />
        <img
          src={`/images/story/${photo.name}.jpg`}
          alt={photo.alt}
          width={photo.width}
          height={photo.height}
          loading="lazy"
          decoding="async"
        />
      </picture>
      {photo.caption && <figcaption>{photo.caption}</figcaption>}
    </figure>
  );
}

function categoryIcon(category: string) {
  if (category === "zazitek") return Sparkles;
  if (category === "vzpominky") return Camera;
  if (category === "domov") return Home;
  return Heart;
}

function formatWeddingDate(value: string) {
  if (!value) return "Datum doplníme";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Datum doplníme";
  return new Intl.DateTimeFormat("cs-CZ", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}

function telephoneHref(value: string) {
  const prefix = value.trim().startsWith("+") ? "+" : "";
  return `tel:${prefix}${value.replace(/\D/g, "")}`;
}

function GuestGenderIcon({
  gender,
}: {
  gender: "woman" | "man";
}) {
  return (
    <span
      className={`guest-gender-icon guest-gender-icon--${gender}`}
      aria-hidden="true"
    >
      <PersonStanding />
    </span>
  );
}

function SeatingChart({
  table,
  animationKey,
}: {
  table: SeatingTable;
  animationKey: number;
}) {
  const filledGuests = table.guests.filter((guest) => guest.name.trim());

  return (
    <div
      className="seating-chart"
      key={`${table.number}-${animationKey}`}
      aria-label={`Rozložení stolu ${table.number}`}
    >
      <div className="seating-chart__stage">
        <div className="seating-chart__spinner">
          <div className="seating-chart__table">
            <Wheat aria-hidden="true" />
            <span>Stůl</span>
            <strong>{table.number}</strong>
            <small>{table.seatCount} míst</small>
          </div>
        </div>
        {table.guests.map((guest, index) => {
          const angle = -90 + (360 / table.seatCount) * index;
          const style = {
            "--seat-angle": `${angle}deg`,
            "--seat-angle-inverse": `${-angle}deg`,
            "--seat-delay": `${620 + index * 105}ms`,
          } as CSSProperties;
          const configured = Boolean(guest.name.trim() && guest.gender);
          return (
            <div
              className={`seating-chart__seat${
                configured ? " is-configured" : " is-empty"
              }`}
              style={style}
              key={index}
              aria-label={
                configured
                  ? `${guest.name}, ${
                      guest.gender === "woman" ? "žena" : "muž"
                    }`
                  : `Nevyplněné místo ${index + 1}`
              }
            >
              <span>
                {configured ? (
                  <GuestGenderIcon
                    gender={guest.gender === "woman" ? "woman" : "man"}
                  />
                ) : (
                  <Armchair aria-hidden="true" />
                )}
              </span>
              {configured && <strong>{guest.name}</strong>}
            </div>
          );
        })}
      </div>

      {filledGuests.length > 0 ? (
        <ol className="seating-chart__guest-list">
          {filledGuests.map((guest, index) => (
            <li
              style={
                {
                  "--seat-delay": `${620 + index * 105}ms`,
                } as CSSProperties
              }
              key={`${guest.name}-${index}`}
            >
              <GuestGenderIcon
                gender={guest.gender === "woman" ? "woman" : "man"}
              />
              <span>{guest.name}</span>
            </li>
          ))}
        </ol>
      ) : (
        <p className="seating-chart__empty">
          Jména hostů u tohoto stolu ještě doplňujeme.
        </p>
      )}
    </div>
  );
}

export function HomePage() {
  const [data, setData] = useState<PublicData | null>(null);
  const [error, setError] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [memoryOpen, setMemoryOpen] = useState(false);
  const [selectedGift, setSelectedGift] = useState<Gift | null>(null);
  const [wediaryLinkCopied, setWediaryLinkCopied] = useState(false);
  const [storySlide, setStorySlide] = useState(0);
  const [selectedTableNumber, setSelectedTableNumber] = useState<number | null>(
    null,
  );
  const [seatingAnimationKey, setSeatingAnimationKey] = useState(0);
  const storyCarouselRef = useRef<HTMLDivElement>(null);

  const scrollToStorySlide = (index: number) => {
    const nextIndex = Math.max(0, Math.min(index, storyPhotos.length - 1));
    const carousel = storyCarouselRef.current;
    const target = carousel?.children.item(nextIndex) as HTMLElement | null;

    if (carousel && target) {
      carousel.scrollTo({
        left: target.offsetLeft - carousel.offsetLeft,
        behavior: "smooth",
      });
    }
    setStorySlide(nextIndex);
  };

  const synchronizeStorySlide = () => {
    const carousel = storyCarouselRef.current;
    if (!carousel) return;

    const current = Array.from(carousel.children).reduce(
      (closest, item, index) => {
        const distance = Math.abs(
          (item as HTMLElement).offsetLeft -
            carousel.offsetLeft -
            carousel.scrollLeft,
        );
        return distance < closest.distance ? { index, distance } : closest;
      },
      { index: 0, distance: Number.POSITIVE_INFINITY },
    );
    setStorySlide(current.index);
  };

  const loadData = async () => {
    try {
      setData(await api<PublicData>("/api/public"));
      setError("");
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "Web se nepodařilo načíst.",
      );
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  useEffect(() => {
    document.body.classList.toggle(
      "body--locked",
      memoryOpen || Boolean(selectedGift),
    );
    return () => document.body.classList.remove("body--locked");
  }, [memoryOpen, selectedGift]);

  if (!data) {
    return (
      <main className="loading-screen">
        <Heart className="loading-screen__heart" fill="currentColor" />
        <p>{error || "Připravujeme svatební louku…"}</p>
        {error && (
          <button className="button button--primary" onClick={() => void loadData()}>
            Zkusit znovu
          </button>
        )}
      </main>
    );
  }

  const {
    site,
    program,
    careTeam = [],
    seatingTables = [],
    gifts,
    memories,
  } = data;
  const wediaryConfigured = isWediaryConfigured(site.wediaryUrl);
  const configuredWitnesses = (site.witnesses ?? []).filter((witness) =>
    witness.name.trim(),
  );
  const selectedTable =
    seatingTables.find((table) => table.number === selectedTableNumber) ?? null;

  const copyWediaryLink = async () => {
    if (!wediaryConfigured) return;
    await navigator.clipboard.writeText(site.wediaryUrl);
    setWediaryLinkCopied(true);
    window.setTimeout(() => setWediaryLinkCopied(false), 2000);
  };

  return (
    <div id="top">
      <header className="site-header">
        <Brand />
        <nav className={`site-nav ${menuOpen ? "site-nav--open" : ""}`}>
          <a href="#pribeh" onClick={() => setMenuOpen(false)}>
            Náš příběh
          </a>
          <a href="#program" onClick={() => setMenuOpen(false)}>
            Program
          </a>
          <a href="#sezeni" onClick={() => setMenuOpen(false)}>
            Sezení
          </a>
          <a href="#darky" onClick={() => setMenuOpen(false)}>
            Dárky
          </a>
          <a href="#momentky" onClick={() => setMenuOpen(false)}>
            Momentky
          </a>
          <a href="#vzpominky" onClick={() => setMenuOpen(false)}>
            Vzpomínky
          </a>
        </nav>
        <button
          className="icon-button site-header__menu"
          type="button"
          onClick={() => setMenuOpen((open) => !open)}
          aria-label={menuOpen ? "Zavřít menu" : "Otevřít menu"}
        >
          {menuOpen ? <X /> : <Menu />}
        </button>
      </header>

      <main>
        <section className="hero">
          <picture className="hero__media">
            <source
              media="(max-width: 700px)"
              type="image/avif"
              srcSet="/images/hero-mobile-480.avif 480w, /images/hero-mobile-768.avif 768w"
              sizes="100vw"
            />
            <source
              media="(max-width: 700px)"
              type="image/webp"
              srcSet="/images/hero-mobile-480.webp 480w, /images/hero-mobile-768.webp 768w"
              sizes="100vw"
            />
            <source
              type="image/avif"
              srcSet="/images/hero-720.avif 720w, /images/hero-1280.avif 1280w, /images/hero-1672.avif 1672w"
              sizes="100vw"
            />
            <source
              type="image/webp"
              srcSet="/images/hero-720.webp 720w, /images/hero-1280.webp 1280w, /images/hero-1672.webp 1672w"
              sizes="100vw"
            />
            <img
              src="/images/hero-1280.jpg"
              srcSet="/images/hero-720.jpg 720w, /images/hero-1280.jpg 1280w, /images/hero-1672.jpg 1672w"
              sizes="100vw"
              alt="Dvě pohádkové postavy pod květinovou bránou ve tvaru srdce"
              fetchPriority="high"
            />
          </picture>
          <div className="hero__overlay" />
          <div className="hero__content">
            <span className="hero__eyebrow">{site.eyebrow}</span>
            <h1>
              {site.firstName}
              <span>&</span>
              {site.secondName}
            </h1>
            <p>{site.tagline}</p>
            <div className="hero__actions">
              <a className="button button--primary" href="#program">
                <CalendarDays size={18} />
                Program dne
              </a>
              <a className="button button--ghost" href="#momentky">
                <Images size={18} />
                Společné momentky
              </a>
            </div>
            <div className="hero__meta">
              <span>
                <CalendarDays size={17} />
                {formatWeddingDate(site.weddingDate)}
              </span>
              <span>
                <MapPin size={17} />
                {site.venue}
              </span>
            </div>
          </div>
          <a className="hero__scroll" href="#pribeh" aria-label="Pokračovat níže">
            <ArrowDown />
          </a>
        </section>

        <Countdown weddingDate={site.weddingDate} />

        <section className="story section" id="pribeh">
          <div className="section__inner">
            <div className="story__grid">
              <div className="story__heading">
                <span className="eyebrow">Jak to celé začalo</span>
                <h2>Dva světy.<br />Jedna společná cesta.</h2>
              </div>
              <div className="story__copy">
                <p>{site.story}</p>
                <div className="story__motifs" aria-label="Motivy našeho příběhu">
                  <span><Wheat /> Pekárna</span>
                  <span><Heart /> Koně</span>
                  <span><Sparkles /> Úsměvy</span>
                  <span><Camera /> Momentky</span>
                </div>
              </div>
            </div>
            <div className="story__album">
              <header className="story__album-heading">
                <div>
                  <span className="eyebrow">Z našeho alba</span>
                  <h3>Pár momentů z mnoha</h3>
                </div>
                <p>
                  Jen malý výběr z našeho života. To nejdůležitější se do
                  jednoho alba stejně nevejde.
                </p>
              </header>
              <div
                className="story__carousel-controls"
                role="group"
                aria-label="Ovládání fotoalba"
              >
                <button
                  type="button"
                  onClick={() => scrollToStorySlide(storySlide - 1)}
                  disabled={storySlide === 0}
                  aria-label="Předchozí fotografie"
                  title="Předchozí fotografie"
                >
                  <ChevronLeft />
                </button>
                <div className="story__carousel-dots">
                  {storyPhotos.map((photo, index) => (
                    <button
                      type="button"
                      className={
                        index === storySlide
                          ? "story__carousel-dot is-active"
                          : "story__carousel-dot"
                      }
                      onClick={() => scrollToStorySlide(index)}
                      aria-label={`Zobrazit fotografii ${index + 1}`}
                      aria-current={index === storySlide ? "true" : undefined}
                      key={photo.name}
                    />
                  ))}
                </div>
                <output aria-live="polite">
                  {storySlide + 1} / {storyPhotos.length}
                </output>
                <button
                  type="button"
                  onClick={() => scrollToStorySlide(storySlide + 1)}
                  disabled={storySlide === storyPhotos.length - 1}
                  aria-label="Další fotografie"
                  title="Další fotografie"
                >
                  <ChevronRight />
                </button>
              </div>
              <div
                className="story__photos"
                aria-label="Výběr rodinných momentek"
                aria-roledescription="carousel"
                onScroll={synchronizeStorySlide}
                ref={storyCarouselRef}
              >
                {storyPhotos.map((photo) => (
                  <StoryPhotoCard photo={photo} key={photo.name} />
                ))}
              </div>
              <div
                className="story__featured"
                aria-label="Další společné rodinné fotografie"
              >
                {featuredStoryPhotos.map((photo) => (
                  <StoryPhotoCard
                    photo={photo}
                    featured
                    key={photo.name}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="schedule section" id="program">
          <div className="section__inner">
            <header className="section-heading section-heading--split">
              <div>
                <span className="eyebrow">Svatební den</span>
                <h2>Kdy, kde a co nás čeká</h2>
              </div>
              <p>Všechno podstatné pohromadě, aby vám zbylo víc času na oslavu.</p>
            </header>
            <div className="schedule__layout">
              <ol className="timeline">
                {program.map((item) => (
                  <li key={`${item.time}-${item.title}`}>
                    <time>{item.time}</time>
                    <span className="timeline__dot" />
                    <div>
                      <strong>{item.title}</strong>
                      <small>{item.detail}</small>
                    </div>
                  </li>
                ))}
              </ol>
              <div className="schedule__side">
                <aside className="practical">
                  <div>
                    <MapPin />
                    <span>Místo</span>
                    <strong>{site.venue}</strong>
                    <small>{site.venueDetail}</small>
                    <div className="practical__links">
                      {site.mapUrl && (
                        <a href={site.mapUrl} target="_blank" rel="noreferrer">
                          Navigovat <ChevronRight size={16} />
                        </a>
                      )}
                      {site.venueUrl && (
                        <a href={site.venueUrl} target="_blank" rel="noreferrer">
                          Web místa <ExternalLink size={15} />
                        </a>
                      )}
                    </div>
                  </div>
                  <div
                    className={
                      configuredWitnesses.length
                        ? undefined
                        : "practical__dress--full"
                    }
                  >
                    <Users />
                    <span>Dress code</span>
                    <strong>{site.dressCode}</strong>
                    <small>Hlavně se přijďte cítit dobře.</small>
                  </div>
                  {configuredWitnesses.length > 0 && (
                    <div className="practical__witnesses">
                      <MessageCircleHeart />
                      <span>Svědkové</span>
                      <div className="witness-list">
                        {configuredWitnesses.map((witness, index) => (
                          <div className="witness-contact" key={index}>
                            <div>
                              <strong>{witness.name}</strong>
                              <em>
                                {witness.side === "marketa"
                                  ? "Za Markétu"
                                  : "Za Tomáše"}
                              </em>
                            </div>
                            {witness.phone && (
                              <a href={telephoneHref(witness.phone)}>
                                <Phone size={15} />
                                {witness.phone}
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                      <small>Pro otázky před svatebním dnem.</small>
                    </div>
                  )}
                </aside>
                {careTeam.length > 0 && (
                  <section
                    className="care-team"
                    aria-labelledby="care-team-title"
                  >
                    <header>
                      <span className="eyebrow">V dobrých rukou</span>
                      <h3 id="care-team-title">
                        Kdo se o nás bude starat
                      </h3>
                    </header>
                    <ol>
                      {careTeam.map((member, index) => (
                        <li key={`${member.title}-${index}`}>
                          <span className="care-team__dot" aria-hidden="true" />
                          <div>
                            <strong>{member.title}</strong>
                            <p>{member.description}</p>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </section>
                )}
              </div>
            </div>
          </div>
        </section>

        {seatingTables.length > 0 && (
          <section className="seating section" id="sezeni">
            <div className="section__inner">
              <header className="section-heading section-heading--split">
                <div>
                  <span className="eyebrow">Zasedací pořádek</span>
                  <h2>Kde budeme sedět</h2>
                </div>
                <p>
                  Vyberte číslo stolu a podívejte se, s kým budete sdílet
                  svatební tabuli.
                </p>
              </header>
              <div className="seating__layout">
                <aside className="seating__chooser">
                  <span>Vyberte svůj stůl</span>
                  <div role="tablist" aria-label="Čísla stolů">
                    {seatingTables.map((table) => (
                      <button
                        type="button"
                        role="tab"
                        aria-selected={selectedTableNumber === table.number}
                        aria-controls="seating-table-display"
                        className={
                          selectedTableNumber === table.number
                            ? "is-active"
                            : ""
                        }
                        onClick={() => {
                          setSelectedTableNumber(table.number);
                          setSeatingAnimationKey((value) => value + 1);
                        }}
                        key={table.number}
                      >
                        <strong>{table.number}</strong>
                        <small>{table.seatCount} míst</small>
                      </button>
                    ))}
                  </div>
                  <small>
                    Číslo stolu najdete také na svém místě během hostiny.
                  </small>
                </aside>
                <div
                  className="seating__display"
                  id="seating-table-display"
                  role="tabpanel"
                  aria-live="polite"
                >
                  {selectedTable ? (
                    <SeatingChart
                      table={selectedTable}
                      animationKey={seatingAnimationKey}
                    />
                  ) : (
                    <div className="seating__placeholder">
                      <Armchair aria-hidden="true" />
                      <strong>Začněte výběrem čísla stolu</strong>
                      <span>
                        Rozložení hostů se potom objeví přímo před vámi.
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        <section className="gifts section" id="darky">
          <div className="section__inner">
            <header className="section-heading section-heading--split">
              <div>
                <span className="eyebrow">Přání do společného života</span>
                <h2>Radost, která se nekoupí dvakrát</h2>
              </div>
              <p>
                Největším darem je, že budete s námi. Pro ty, kdo chtějí přidat
                něco navíc, jsme připravili několik přání.
              </p>
            </header>
            <div className="gift-grid">
              {gifts.map((gift) => {
                const Icon = categoryIcon(gift.category);
                return (
                  <article className="gift-card" key={gift.id}>
                    <div className="gift-card__icon"><Icon /></div>
                    <h3>{gift.title}</h3>
                    <p>{gift.description}</p>
                    <button
                      className={
                        gift.status === "available"
                          ? "button button--text"
                          : "gift-card__reserved"
                      }
                      disabled={gift.status !== "available"}
                      onClick={() => setSelectedGift(gift)}
                    >
                      {gift.status === "available" ? (
                        <>Vybrat přání <ChevronRight size={17} /></>
                      ) : (
                        <><Check size={16} /> V dobrých rukou</>
                      )}
                    </button>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="moments" id="momentky">
          <div className="section__inner moments__inner">
            <div className="moments__copy">
              <span className="eyebrow">MakyTom momentky · MyWediary</span>
              <h2>Vyfoťte to, co profesionální fotograf nezahlédne.</h2>
              <p>
                Své momentky přidáte na samostatné stránce MyWediary, kterou
                otevřete tlačítkem nebo QR kódem.
              </p>
              <div className="moments__facts" aria-label="Výhody galerie">
                <span><Smartphone /> V prohlížeči</span>
                <span><Images /> Samostatná galerie</span>
                <span><ScanLine /> Jeden QR kód</span>
              </div>
              {wediaryConfigured ? (
                <div className="moments__actions">
                  <a
                    className="button button--primary"
                    href={site.wediaryUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Camera size={18} />
                    Otevřít MyWediary
                    <ExternalLink size={16} />
                  </a>
                  <button
                    className="button button--ghost"
                    type="button"
                    onClick={() => void copyWediaryLink()}
                  >
                    {wediaryLinkCopied ? <Check size={18} /> : <Copy size={18} />}
                    {wediaryLinkCopied ? "Odkaz zkopírován" : "Kopírovat odkaz"}
                  </button>
                </div>
              ) : (
                <p className="moments__pending">
                  Soukromý odkaz galerie právě připravujeme.
                </p>
              )}
              <small className="moments__note">
                Na místě je potřeba internet. Když signál zlobí, momentky můžete
                nahrát později.
              </small>
            </div>
            {wediaryConfigured ? (
              <a
                className="moments__qr"
                href={site.wediaryUrl}
                target="_blank"
                rel="noreferrer"
                aria-label="Otevřít MyWediary pomocí QR kódu"
              >
                <QRCodeSVG
                  value={site.wediaryUrl}
                  size={188}
                  bgColor="#ffffff"
                  fgColor="#17392b"
                  level="M"
                  title="QR kód MyWediary galerie"
                />
                <strong>MYWEDIARY</strong>
                <span>Naskenujte fotoaparátem</span>
              </a>
            ) : (
              <div className="moments__qr moments__qr--pending" aria-hidden="true">
                <ScanLine />
                <strong>MYWEDIARY</strong>
                <span>Čeká na propojení</span>
              </div>
            )}
          </div>
        </section>

        <section className="memories section" id="vzpominky">
          <div className="section__inner">
            <header className="section-heading section-heading--split">
              <div>
                <span className="eyebrow">Vzpomínky a přání</span>
                <h2>Pár slov, která s námi zůstanou</h2>
              </div>
              <button
                className="button button--outline"
                onClick={() => setMemoryOpen(true)}
              >
                <MessageCircleHeart size={18} />
                Přidat vzpomínku
              </button>
            </header>
            <div className="memory-grid">
              {memories.slice(0, 6).map((memory, index) => (
                <article
                  className={`memory-note memory-note--${(index % 3) + 1}`}
                  key={memory.id}
                >
                  <Heart size={18} fill="currentColor" />
                  <h3>{memory.title}</h3>
                  <p>{memory.body}</p>
                  <span>— {memory.author}</span>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="section__inner site-footer__inner">
          <Brand compact />
          <p>Uvidíme se na svatební louce.</p>
          <span>makyatom.fun</span>
        </div>
      </footer>

      <GiftDialog
        gift={selectedGift}
        onClose={() => setSelectedGift(null)}
        onReserved={() => void loadData()}
      />
      <MemoryDialog
        open={memoryOpen}
        onClose={() => setMemoryOpen(false)}
      />
    </div>
  );
}

function GiftDialog({
  gift,
  onClose,
  onReserved,
}: {
  gift: Gift | null;
  onClose: () => void;
  onReserved: () => void;
}) {
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!gift) return;
    setBusy(true);
    setError("");
    try {
      const result = await api<{ token: string }>(
        `/api/gifts/${gift.id}/reserve`,
        { method: "POST", body: JSON.stringify({ name }) },
      );
      localStorage.setItem(`makyatom-gift-${gift.id}`, result.token);
      setMessage("Přání je pro vás rezervované.");
      onReserved();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Rezervace selhala.");
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setName("");
    setMessage("");
    setError("");
    onClose();
  };

  return (
    <Dialog open={Boolean(gift)} title={gift?.title || "Rezervace"} onClose={close}>
      {message ? (
        <div className="success-state">
          <span><GiftIcon /></span>
          <h3>Je v dobrých rukou</h3>
          <p>{message}</p>
          <button className="button button--primary" onClick={close}>Hotovo</button>
        </div>
      ) : (
        <form className="form-stack" onSubmit={submit}>
          <p className="form-intro">{gift?.description}</p>
          <label>
            Vaše jméno
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              minLength={2}
              maxLength={120}
              required
              autoFocus
            />
          </label>
          <p className="privacy-note">
            <ShieldCheck /> Jméno uvidí pouze novomanželé.
          </p>
          {error && <p className="form-error">{error}</p>}
          <button className="button button--primary" disabled={busy}>
            {busy ? "Rezervuji…" : "Rezervovat přání"}
          </button>
        </form>
      )}
    </Dialog>
  );
}

function MemoryDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      const result = await api<{ message: string }>("/api/memories", {
        method: "POST",
        body: JSON.stringify({
          author: form.get("author"),
          title: form.get("title"),
          body: form.get("body"),
          visibility: form.get("visibility"),
        }),
      });
      setMessage(result.message);
      event.currentTarget.reset();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Odeslání selhalo.");
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setMessage("");
    setError("");
    onClose();
  };

  return (
    <Dialog open={open} title="Přidat vzpomínku" onClose={close}>
      {message ? (
        <div className="success-state">
          <span><MessageCircleHeart /></span>
          <h3>Děkujeme za pár slov</h3>
          <p>{message}</p>
          <button className="button button--primary" onClick={close}>Hotovo</button>
        </div>
      ) : (
        <form className="form-stack" onSubmit={submit}>
          <div className="form-row">
            <label>
              Vaše jméno
              <input name="author" minLength={2} maxLength={120} required />
            </label>
            <label>
              Nadpis
              <input name="title" minLength={3} maxLength={120} required />
            </label>
          </div>
          <label>
            Vzpomínka nebo přání
            <textarea name="body" rows={7} minLength={10} maxLength={1500} required />
          </label>
          <fieldset className="segmented segmented--two">
            <legend>Kdo si může zprávu přečíst?</legend>
            <label>
              <input type="radio" name="visibility" value="public" defaultChecked />
              <span>Hosté i novomanželé</span>
            </label>
            <label>
              <input type="radio" name="visibility" value="private" />
              <span>Jen novomanželé</span>
            </label>
          </fieldset>
          <p className="privacy-note">
            <ShieldCheck /> Veřejná vzpomínka se zobrazí až po schválení.
          </p>
          {error && <p className="form-error">{error}</p>}
          <button className="button button--primary" disabled={busy}>
            {busy ? "Odesílám…" : "Odeslat ke schválení"}
          </button>
        </form>
      )}
    </Dialog>
  );
}
